/**
 * One cohort × orientation pairing in the expanded row: the derived
 * status rollup, "N of M assigned", per-member state with derived skip
 * reasons (see lib/cohortEntity), Cancel for Active pairings — and the
 * pairing's GROUP RUNS (group plan Phase 5): a Create-group entry plus
 * one GroupRunRow per run.
 */
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { memberPairingState, regroupBlockReason } from '@/lib/cohortEntity';
import type { AssignmentSlice } from '@/lib/cohortEntity';
import { ledRunName } from '@/lib/groupRunEntity';
import type { CohortEntityRow, PairingRow } from '@/hooks/useCohortEntities';
import type { GroupRunRowData } from '@/hooks/useGroupRuns';
import { PairingStatusPill } from './atoms';
import { GroupRunRow } from './GroupRunRow';
import { GatedButton } from '../GatedButton';

const MEMBER_STATUS_LABEL: Record<string, string> = {
  NotStarted: 'not started',
  InProgress: 'in progress',
  Completed: 'completed',
  Cancelled: 'cancelled',
};

export type GroupRunActions = {
  onStart: (run: GroupRunRowData) => void;
  onPause: (run: GroupRunRowData) => void;
  onResume: (run: GroupRunRowData) => void;
  onHandOver: (run: GroupRunRowData, newLeaderEmail: string) => void;
  onCancelRun: (run: GroupRunRowData) => void;
  onJoin: (run: GroupRunRowData) => void;
  /** Lobby only: update_group_run with changed settings (Phase 10). */
  onUpdateSettings: (
    run: GroupRunRowData,
    settings: { allowLateJoin: boolean; autoStallSeconds: number },
  ) => void;
};

export function PairingDetail({
  cohort,
  pairing: p,
  orientationName,
  assignmentSlices,
  groupRuns,
  currentUserEmail,
  busy,
  onCancel,
  onCreateGroup,
  runActions,
}: {
  cohort: CohortEntityRow;
  pairing: PairingRow;
  orientationName: string;
  assignmentSlices: AssignmentSlice[];
  /** Runs whose cohortAssignmentId === this pairing's. */
  groupRuns: GroupRunRowData[];
  currentUserEmail: string;
  busy: boolean;
  onCancel: () => void;
  /** Opens the Regroup drawer with the pairing's ASSIGNED members and the
   *  hover reason for each one that can't be grouped (Samuel 2026-09-21:
   *  blocked members render disabled, never silently omitted). */
  onCreateGroup: (
    assignedEmails: string[],
    blockedReasons: Map<string, string>,
  ) => void;
  runActions: GroupRunActions;
}) {
  const states = cohort.members.map((email) => ({
    email,
    state: memberPairingState(
      email,
      { cohortAssignmentId: p.cohortAssignmentId, orientationId: p.orientationId },
      assignmentSlices,
    ),
  }));
  const assignedCount = states.filter((s) => s.state.kind === 'assigned').length;
  // Groupable = assigned + NotStarted + not already in one of this pairing's
  // runs (the module refuses the rest; offering them only harvests refusals).
  const groupedAssignmentIds = new Set(
    groupRuns
      .filter((r) => r.statusTag !== 'Cancelled')
      .flatMap((r) => r.members.map((m) => String(m.assignmentId))),
  );
  const assignedStates = states.filter(
    (s): s is typeof s & { state: { kind: 'assigned' } } => s.state.kind === 'assigned',
  );
  const regroupBlockReasons = new Map<string, string>();
  for (const s of assignedStates) {
    if (s.state.kind !== 'assigned') continue;
    const reason = regroupBlockReason({
      email: s.email,
      orientationName,
      status: s.state.status,
      inGroupOfThisPairing: groupedAssignmentIds.has(String(s.state.assignmentId)),
    });
    if (reason) regroupBlockReasons.set(s.email, reason);
  }
  const assignedEmails = assignedStates.map((s) => s.email);
  const groupableEmails = assignedEmails.filter((e) => !regroupBlockReasons.has(e));

  return (
    <div style={{ borderLeft: '2px solid var(--amber, var(--good))', paddingLeft: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: 12.5 }}>{orientationName}</b>
        <PairingStatusPill status={p.displayStatus} />
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {assignedCount} of {cohort.members.length} assigned · by {p.assignedByEmail}
          {p.displayStatus === 'Cancelled' && p.cancelledBy
            ? ` · cancelled by ${p.cancelledBy}`
            : ''}
        </span>
        {p.storedStatus === 'Active' ? (
          <button
            type="button"
            className="btn sm danger"
            style={{ marginLeft: 'auto' }}
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
      </div>
      <div style={{ marginTop: 4, display: 'grid', gap: 2 }}>
        {states.map(({ email, state }) => {
          // A roster leader takes no assignment under the pairing
          // (assign-with-groups) — or holds an untouched NotStarted one
          // from the regroup path. Either way their line is "leads
          // <group>", not a warning or "not started".
          const leads = ledRunName(email, groupRuns);
          const showLeads =
            leads !== null &&
            (state.kind === 'not-assigned' ||
              (state.kind === 'assigned' && state.status === 'NotStarted'));
          return (
          <div
            key={email}
            style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}
          >
            <span style={{ minWidth: 220 }}>{email}</span>
            {showLeads ? (
              <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                leads “{leads}”
              </span>
            ) : state.kind === 'assigned' ? (
              <span style={{ color: 'var(--muted)', fontSize: 11.5 }}>
                {MEMBER_STATUS_LABEL[state.status] ?? state.status}
                {' · '}#{String(state.assignmentId)}
              </span>
            ) : (
              <span style={{ color: 'var(--warn)', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Icon of={Icons.status.warning} size={12} /> {state.reason}
              </span>
            )}
          </div>
          );
        })}
      </div>

      {p.storedStatus === 'Active' ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
              Groups
            </span>
            {/* #191: an archived cohort can still hold a live pairing
                ("archive, let them finish") — its runs stay visible and
                controllable, but the roster is frozen so no NEW group. */}
            <GatedButton
              className="btn sm ghost"
              hidden={cohort.archived}
              busy={busy || cohort.archived}
              reason={
                groupableEmails.length === 0
                  ? 'No groupable members — everyone assigned here has started, or is already in a group.'
                  : null
              }
              hint="After-the-fact regrouping — a late joiner, a split. The normal way in is the group layout on the Orientation Assignment page."
              onClick={() => onCreateGroup(assignedEmails, regroupBlockReasons)}
            >
              + Regroup
            </GatedButton>
          </div>
          {groupRuns.map((run) => (
            <GroupRunRow
              key={String(run.groupRunId)}
              run={run}
              busy={busy}
              currentUserEmail={currentUserEmail}
              onStart={() => runActions.onStart(run)}
              onPause={() => runActions.onPause(run)}
              onResume={() => runActions.onResume(run)}
              onHandOver={(email) => runActions.onHandOver(run, email)}
              onCancel={() => runActions.onCancelRun(run)}
              onJoin={() => runActions.onJoin(run)}
              onUpdateSettings={(settings) => runActions.onUpdateSettings(run, settings)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
